import React from 'react';
import { Plus, Search, Filter, AlertTriangle, Users, Mail, Shield } from 'lucide-react';

const EmptyState: React.FC = () => (
  <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
    <Users className="h-10 w-12 text-slate-400 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-slate-900 mb-2">No users available</h3>
    <p className="text-slate-600 mb-6">
      No user management data found. Connect to user management API to see user accounts.
    </p>
    <button className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors">
      <Plus className="h-4 w-4 mr-2" />
      Add First User
    </button>
  </div>
);

// const RoleBadge: React.FC<{ role: string }> = ({ role }) => {
//   const config = {
//     admin: { color: 'bg-red-100 text-red-800' },
//     user: { color: 'bg-slate-100 text-blue-800' },
//     viewer: { color: 'bg-gray-100 text-gray-800' },
//   };

//   const { color } = config[role as keyof typeof config] || config.user;

//   return (
//     <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${color}`}>
//       {role}
//     </span>
//   );
// };

export const UserManagementPage: React.FC = () => {
  // In real implementation, this would fetch from API
  const users: Record<string, unknown>[] = []; // No mock data - will show empty state
  const loading = false;
  const error = null;

  if (error) {
    return (
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">User Management</h1>
            <p className="text-slate-600 mt-1">Manage user accounts and permissions</p>
          </div>
          <button className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors">
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </button>
        </div>

        <div className="bg-white rounded-lg border border-red-200 p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-red-900">Error Loading Users</h3>
              <p className="text-red-700 mt-1">Unable to connect to user management API. Please check your configuration.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">User Management</h1>
          <p className="text-slate-600 mt-1">Manage user accounts and permissions</p>
        </div>
        <button className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors">
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search users by name, email, or company..."
                className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
              <option value="viewer">Viewer</option>
            </select>
            <select className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
            <button className="inline-flex items-center px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">
              <Filter className="h-4 w-4 mr-1" />
              Filter
            </button>
          </div>
        </div>
      </div>

      {/* User List */}
      {loading ? (
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading users...</p>
        </div>
      ) : users.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-medium text-slate-900">Users</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Last Active
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {/* Table rows would be rendered here from real data */}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-slate-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-slate-900">Total Users</h3>
              <p className="text-2xl font-semibold text-slate-900">No data available</p>
              <p className="text-sm text-slate-500">Connect to user API</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Shield className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-slate-900">Active Users</h3>
              <p className="text-2xl font-semibold text-slate-900">No data available</p>
              <p className="text-sm text-slate-500">Last 30 days</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Mail className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-slate-900">Pending Invites</h3>
              <p className="text-2xl font-semibold text-slate-900">No data available</p>
              <p className="text-sm text-slate-500">Awaiting acceptance</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
